/**
 * TopBar — always-visible navigation bar at the top.
 *
 * Mobile (<768px):  ☰  Enterprise · Betrieb · 🟢 Role  👤
 * Desktop (≥1280px): Logo  Enterprise ▼  Betrieb ▼  🟢 Role  🔍  🔔  👤
 *
 * See: docs/architecture/UX_NAVIGATION_PATTERNS.md (v3) §3
 */

import { useTranslation } from 'react-i18next';
import { motion } from 'motion/react';
import { useNavigationProfile } from '../context/NavigationContext';
import { Dropdown } from '../components/overlay';


const ROLE_COLORS = {
  green:  'bg-green-600',
  blue:   'bg-blue-600',
  gray:   'bg-gray-500',
  yellow: 'bg-yellow-500',
  orange: 'bg-orange-500',
  purple: 'bg-purple-600',
  teal:   'bg-teal-600',
};

function RoleBadge({ label, color }) {
  const cls = ROLE_COLORS[color] || ROLE_COLORS.green;
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold text-white ${cls}`}
      data-testid="role-badge"
    >
      <span className="w-1.5 h-1.5 rounded-full bg-white/70 flex-shrink-0" />
      {label}
    </span>
  );
}

export default function TopBar({
  // navigation profile (optional prop — falls back to NavigationContext)
  profile: profileProp,
  // enterprise / betrieb context
  enterprise,
  betrieb,
  onOpenEnterpriseSwitcher,
  // hamburger menu
  onHamburger,
  // right-side actions
  unreadCount,
  onBellClick,
  onProfileClick,
  onOpenWallet,
  // quick create (desktop "+Neu" dropdown)
  onQuickCreate,
  // search
  onSearch,
  // branding
  appName,
  // Account menu wrapper (renders around avatar button)
  AvatarWrapper,
}) {
  const { t } = useTranslation();
  const { profile: ctxProfile } = useNavigationProfile();
  const profile = profileProp ?? ctxProfile;
  const roleBadgeColor = profile?.role_badge_color || 'green';
  const roleLabel = profile?.role_label || '';
  const showSwitcher = profile?.show_enterprise_switcher !== false;
  const showNotifications = profile?.show_notifications !== false;
  const showSearch = profile?.show_search !== false;

  const entName = enterprise?.name || appName || 'AI Farm';
  const betName = betrieb?.name || '';

  // Compose the compact context string for mobile
  const contextParts = [entName];
  if (betName) contextParts.push(betName);

  return (
    <header
      className="flex items-center justify-between gap-3 px-3 h-12 flex-shrink-0
                 bg-[var(--aif-bg-surface)] border-b border-[var(--aif-border-default)]"
      data-testid="top-bar"
    >
      {/* ── Left ─────────────────────────────────────────────────────────── */}
      <div className="flex items-center gap-2 min-w-0">
        {/* Hamburger */}
        <button
          onClick={onHamburger}
          className="w-9 h-9 flex-shrink-0 flex items-center justify-center rounded-lg
                     text-[var(--aif-text-muted)] hover:bg-[var(--aif-bg-elevated)] transition"
          aria-label={t('ui.menu')}
          data-testid="topbar-hamburger"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h7" />
          </svg>
        </button>

        {/* Enterprise › Betrieb context — recognizable button with role dot */}
        <motion.button
          className={`flex items-center gap-2 min-w-0 text-left px-3 py-1.5 rounded-[var(--aif-radius-md)] text-[13px] transition ${
            showSwitcher
              ? 'bg-[var(--aif-bg-tertiary)] border border-[var(--aif-border-subtle)] hover:border-[var(--aif-border-default)] cursor-pointer'
              : ''
          }`}
          onClick={showSwitcher ? onOpenEnterpriseSwitcher : undefined}
          disabled={!showSwitcher}
          whileTap={showSwitcher ? { scale: 0.98 } : undefined}
          data-testid="topbar-context"
        >
          {/* Role color dot */}
          <span
            className={`w-2 h-2 rounded-full flex-shrink-0 ${ROLE_COLORS[roleBadgeColor] || ROLE_COLORS.green}`}
            data-testid="topbar-role-dot"
          />
          {/* Enterprise name — hidden on narrow mobile */}
          <span className="font-medium text-[var(--aif-text-primary)] truncate max-w-[120px] sm:max-w-[160px] hidden sm:inline">
            {entName}
          </span>
          {/* › separator — desktop only */}
          {betName && (
            <span className="text-[var(--aif-text-tertiary)] flex-shrink-0 hidden sm:inline px-0.5">›</span>
          )}
          {/* Betrieb name — always visible */}
          <span className="text-[var(--aif-text-secondary)] truncate max-w-[100px] sm:max-w-[120px]">
            {betName || entName}
          </span>
          {/* ▼ chevron */}
          {showSwitcher && (
            <svg className="w-3 h-3 text-[var(--aif-text-tertiary)] flex-shrink-0" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z" clipRule="evenodd" />
            </svg>
          )}
        </motion.button>
      </div>

      {/* ── Right — equalized 32×32 icon containers ─────────────────────── */}
      <div className="flex items-center gap-1 flex-shrink-0">
        {/* UX-P2: Desktop "+Neu" quick-create dropdown */}
        {onQuickCreate && (
          <Dropdown.Root>
            <Dropdown.Trigger asChild>
              <button
                className="hidden md:flex items-center gap-1 px-2.5 py-1 rounded-[var(--aif-radius-md)]
                           bg-[var(--aif-color-primary)] text-white text-[12px] font-medium
                           hover:opacity-90 transition"
                data-testid="topbar-quick-create"
              >
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
                </svg>
                {t('quickCreate.newButton')}
              </button>
            </Dropdown.Trigger>
            <Dropdown.Content side="bottom" align="end">
              <Dropdown.Item onSelect={() => onQuickCreate('note')} data-testid="quick-create-note">
                📝 {t('quickCreate.note')}
              </Dropdown.Item>
              <Dropdown.Item onSelect={() => onQuickCreate('task')} data-testid="quick-create-task">
                ✅ {t('quickCreate.task')}
              </Dropdown.Item>
              <Dropdown.Item onSelect={() => onQuickCreate('scouting')} data-testid="quick-create-scouting">
                🔍 {t('quickCreate.scouting')}
              </Dropdown.Item>
              <Dropdown.Item onSelect={() => onQuickCreate('poi')} data-testid="quick-create-poi">
                📍 {t('quickCreate.poi')}
              </Dropdown.Item>
            </Dropdown.Content>
          </Dropdown.Root>
        )}

        {/* A3-4: Cmd+K Command Palette trigger (respects show_search profile field) */}
        {showSearch && <button
          type="button"
          data-testid="cmd-k-trigger"
          onClick={() => {
            window.dispatchEvent(new CustomEvent('aif:open-command-palette'));
          }}
          title={t('cmd.trigger', 'Command Palette (Cmd+K)')}
          aria-label={t('cmd.trigger', 'Command Palette (Cmd+K)')}
          className="hidden sm:flex items-center gap-1 px-2 py-1 rounded-[var(--aif-radius-md)]
                     border border-[var(--aif-border-default)] text-[11px]
                     text-[var(--aif-text-muted)] hover:bg-[var(--aif-bg-tertiary)] transition"
        >
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
          </svg>
          <kbd style={{ fontFamily: 'inherit' }}>&#8984;K</kbd>
        </button>}

        {/* Notifications bell — 32×32 container, 20×20 icon */}
        {showNotifications && onBellClick && (
          <button
            onClick={onBellClick}
            className="relative w-8 h-8 flex items-center justify-center rounded-[var(--aif-radius-md)]
                       text-[var(--aif-text-secondary)] hover:bg-[var(--aif-bg-tertiary)] hover:text-[var(--aif-text-primary)] transition"
            aria-label={t('app.notificationTitle')}
            data-testid="topbar-bell"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round"
                d="M14.857 17.082a23.848 23.848 0 005.454-1.31A8.967 8.967 0 0118 9.75v-.7V9A6 6 0 006 9v.75a8.967 8.967 0 01-2.312 6.022c1.733.64 3.56 1.085 5.455 1.31m5.714 0a24.255 24.255 0 01-5.714 0m5.714 0a3 3 0 11-5.714 0" />
            </svg>
            {unreadCount > 0 && (
              <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 text-[10px] font-medium
                               bg-[var(--aif-color-danger)] text-white rounded-full
                               flex items-center justify-center leading-none"
                data-testid="topbar-bell-badge">
                {unreadCount > 9 ? '9+' : unreadCount}
              </span>
            )}
          </button>
        )}

        {/* Profile avatar — 32×32 container, 20×20 icon */}
        {AvatarWrapper ? (
          <AvatarWrapper>
            <button
              className="w-8 h-8 flex items-center justify-center rounded-[var(--aif-radius-md)]
                         text-[var(--aif-text-secondary)] hover:bg-[var(--aif-bg-tertiary)] hover:text-[var(--aif-text-primary)] transition"
              aria-label={t('nav.profile')}
              data-testid="topbar-profile"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round"
                  d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z" />
              </svg>
            </button>
          </AvatarWrapper>
        ) : (
          <button
            onClick={onProfileClick}
            className="w-8 h-8 flex items-center justify-center rounded-[var(--aif-radius-md)]
                       text-[var(--aif-text-secondary)] hover:bg-[var(--aif-bg-tertiary)] hover:text-[var(--aif-text-primary)] transition"
            aria-label={t('nav.profile')}
            data-testid="topbar-profile"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round"
                d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z" />
            </svg>
          </button>
        )}
      </div>
    </header>
  );
}
